/**
 * Plausible Analytics wrapper for easy local debugging and safe execution.
 * @param {string} event - event name
 * @param {object} props - event properties
 * @param {object} options - extra Plausible options, e.g. `{ interactive: false }`
 */
function trackAnalyticsEvent(event, props = {}, options = {}) {
  if (typeof window.plausible === 'function') {
    window.plausible(event, { props, ...options });
  } else {
    // only in dev mode
    if (location.hostname === 'localhost') {
      console.info('[analytics]', event, props);
    }
  }
}

window.trackAnalyticsEvent = trackAnalyticsEvent;

/**
 * Report Content Security Policy violations as Plausible events.
 *
 * There is no `report-uri` endpoint (see docs/SECURITY-HEADERS.md), so without this a broken CSP —
 * a stale inline-code hash, a new embed nobody allowlisted — fails silently in production and only
 * ever shows up in the visitor's own console.
 *
 * The listener lives in the inline snippet in components/analytics.njk, because most violations
 * fire while the document is still parsing, long before this deferred bundle runs. That snippet
 * only buffers; everything below decides what is worth sending.
 */

/** Extension-injected scripts and styles violate the policy constantly and say nothing about this site. */
const EXTENSION_SCHEME = /^[a-z-]*(extension|resource):/i;
/** Cap per page load — one broken directive can otherwise fire hundreds of identical events. */
const MAX_CSP_REPORTS = 5;

function reportCspViolations() {
  const seen = new Set();

  const report = (event) => {
    const blocked = (event.blockedURI || 'inline').split(/[?#]/)[0];
    const directive = event.effectiveDirective || event.violatedDirective || 'unknown';
    const key = `${directive}|${blocked}`;

    if (EXTENSION_SCHEME.test(blocked) || seen.has(key) || seen.size >= MAX_CSP_REPORTS) {
      return;
    }
    seen.add(key);

    // Non-interactive: a violation is not something the visitor did, so it must not turn a
    // bounce into an engaged visit (it did, while giscus' stylesheet was blocked: bounce 79 % -> 37 %).
    trackAnalyticsEvent('CSP Violation', { directive, blocked, page: location.pathname }, { interactive: false });
  };

  // Same handoff the Plausible stub uses: swap the queue for something that reports straight away,
  // then drain whatever the inline listener collected before this ran.
  const buffered = window.cspViolations || [];
  window.cspViolations = { push: report };
  buffered.forEach(report);
}

/**
 * On the 404 page (src/pages/404.njk), report which URL was not found. Cloudflare Pages serves
 * 404.html under the requested URL, so location.pathname is the missing path: old Gatsby assets,
 * mistyped topic slugs, bot probes. Plausible counts the pageview either way; the event makes the
 * 404s filterable (goal `404`, property `path`) instead of hiding among real pages.
 */
function reportNotFound() {
  if (document.querySelector('[data-not-found]')) {
    // Non-interactive like CSP Violation: landing on a 404 is not something the visitor did on the page
    trackAnalyticsEvent('404', { path: location.pathname }, { interactive: false });
  }
}

(() => {
  reportCspViolations();
  reportNotFound();

  // Global click listener for multiple trackers
  document.addEventListener('click', (e) => {
    // 1. Social Links (Unified)
    const socialLink = e.target.closest('[data-social-name]');
    if (socialLink) {
      trackAnalyticsEvent('Social Link Click', {
        name: socialLink.dataset.socialName,
        url: socialLink.getAttribute('href'),
        location: socialLink.dataset.location || 'Unknown',
      });
      return; // Avoid double tracking as outbound link
    }

    // 2. Share Buttons
    const shareCopyBtn = e.target.closest('.share-copy');
    if (shareCopyBtn) {
      trackAnalyticsEvent('Share Post Click', { type: 'Copy Post Link', shareCopyUrl: location.pathname });
      return;
    }

    const nativeShareBtn = e.target.closest('#native-share-button');
    if (nativeShareBtn) {
      trackAnalyticsEvent('Share Post Click', { type: 'Native Share', shareCopyUrl: location.pathname });
      return;
    }

    // 3. Code Block Copy
    const codeCopyBtn = e.target.closest('.copy-code-button');
    if (codeCopyBtn) {
      trackAnalyticsEvent('Code Block Copy Click', { codeBlockUrl: location.pathname });
      return;
    }

    // 6. About Page Links
    const aboutLink = e.target.closest('[data-about-name]');
    if (aboutLink) {
      trackAnalyticsEvent('About Link Click', {
        name: aboutLink.dataset.aboutName,
        url: aboutLink.getAttribute('href'),
      });
      return;
    }

    // 4. Outbound Links
    const link = e.target.closest('a');
    if (link && link.hostname && link.hostname !== window.location.hostname) {
      // Ignore internal links (some links might be relative or full URLs to the same site)
      // and anchor links on the same page
      if (!link.href.startsWith('mailto:') && !link.href.startsWith('tel:')) {
        trackAnalyticsEvent('Outbound Link Click', { url: link.href });
      }
      return;
    }

    // 5. Topic Clicks
    const topicLink = e.target.closest('.topic-name');
    if (topicLink) {
      trackAnalyticsEvent('Topic Click', { topic: topicLink.innerText.trim() });
      return;
    }
  });

  // 7. Video Playback Tracking: plays the reader started. Clips that stand in for GIFs start on
  // their own when scrolled into view (video-autoplay.js marks those plays `data-autoplaying`), and
  // counting them would only measure scrolling.
  const trackVideoEvent = (video, type) => {
    const title = video.dataset.videoTitle || video.getAttribute('title') || 'Untitled Video';
    const src = video.currentSrc || video.querySelector('source')?.getAttribute('src') || 'Unknown Source';

    trackAnalyticsEvent('Video Playback', {
      type,
      title,
      src,
      url: location.pathname,
    });
  };

  document.addEventListener(
    'play',
    (e) => {
      if (e.target.tagName === 'VIDEO' && !('autoplaying' in e.target.dataset)) {
        trackVideoEvent(e.target, 'Play');
      }
    },
    { capture: true },
  );
})();
